import LoadingOutlined from '@ant-design/icons/LoadingOutlined'
import {
  Button,
  Collapse,
  CollapseProps,
  Divider,
  message,
  Spin,
  Typography,
} from 'antd'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { IndicationDetailCardProps } from 'features/indicationTriage/useIndicationDetailCards'
import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import isEmpty from 'lodash/isEmpty'
import React, { FC, useMemo } from 'react'
import styled from 'styled-components'
import { GeckoTheme } from 'styles/GeckoTheme'
import useSWRImmutable from 'swr/immutable'

import WaterfallPlot from './trilatReportSignalCharts/WaterfallPlot'

const { Text, Title: AntTitle } = Typography

const fileFetcher = async (url: string): Promise<number[][]> => {
  const res = fetch(url).then((resp) => resp.json())
  return res
}

const formatPath = (path: string | undefined) => {
  if (path) {
    return path.split('.')[0]
  }
  return ''
}

const useIndicationFiles = (indicationId: string | undefined) => {
  const { fileRefs } = useRolodexFileLS(
    useMemo(
      () =>
        indicationId
          ? {
              entry_id: indicationId,
              path: '/*',
            }
          : undefined,
      [indicationId]
    )
  )
  const { urls, isLoading: rolodexLoading } = useRolodexSignedURLs(fileRefs)

  const allFetch = React.useCallback(
    () =>
      Promise.all(
        urls.map(async (url, index) => ({
          data: await fileFetcher(url),
          filename: formatPath(fileRefs[index].path),
        }))
      ),
    [fileRefs, urls]
  )

  const { data, isLoading } = useSWRImmutable([urls], allFetch)

  const formattedData: Record<string, number[][]> = data
    ? data.reduce((acc: Record<string, number[][]>, current) => {
        acc[current.filename] = current.data
        return acc
      }, {})
    : {}
  return { data: formattedData, isLoading: isLoading || rolodexLoading }
}

type DRAViewProps = {
  bscan0: number[][]
  bscan1: number[][]
  bscan2: number[][]
}

const DRA2View: FC<DRAViewProps> = (props) => {
  const { bscan0, bscan2 } = props

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: 'Backwall attenutation in the 0°',
      children: <DRA2Item1 data={bscan0}></DRA2Item1>,
    },
    {
      key: '2',
      label: 'Visibility of inner diameter in the 45°',
      children: <DRA2Item2 data={bscan2}></DRA2Item2>,
    },
  ]

  return (
    <ColumnWrapper>
      <AntTitle level={4}>Observed Qualities</AntTitle>
      <Collapse items={items} accordion />
      <Divider></Divider>
      <DRA2VerdictItem></DRA2VerdictItem>
      <Divider></Divider>
      <DRA2Actions></DRA2Actions>
    </ColumnWrapper>
  )
}

const DRA2Item1: FC<{ data: number[][] }> = (props) => {
  const { data } = props

  const shape1 = {
    type: 'rect' as const,
    x0: 5,
    x1: 45,
    y0: 700,
    y1: 770,
    line: { color: GeckoTheme.colors.SeaGreen },
  }

  const shape2 = {
    type: 'rect' as const,
    x0: 5,
    x1: 45,
    y0: 430,
    y1: 500,
    line: { color: GeckoTheme.colors.BrightOrange },
  }

  return (
    <div>
      <WaterfallPlot
        dataValues={data}
        width={315}
        height={500}
        shapes={[shape1, shape2]}></WaterfallPlot>
      <Text>
        <ColorSpan color={GeckoTheme.colors.SeaGreen}>The Inclusion </ColorSpan>
        has begun to{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>
          weaken the intensity of the backwall response.{' '}
        </ColorSpan>
        This is an early sign of hydrogen accumulation. In the event of HIC, we
        expect the 0° backwall to be completely attenutated rather than
        partially.
      </Text>
    </div>
  )
}

const DRA2Item2: FC<{ data: number[][] }> = (props) => {
  const { data } = props

  const shape1 = {
    type: 'rect' as const,
    x0: 230,
    x1: 763,
    y0: 590,
    y1: 670,
    line: { color: GeckoTheme.colors.SeaGreen },
  }

  const shape2 = {
    type: 'line' as const,
    x0: 0,
    x1: data.length,
    y0: 287,
    y1: 287,
    line: { color: GeckoTheme.colors.BrightOrange },
  }

  return (
    <div>
      <WaterfallPlot
        dataValues={data}
        width={315}
        height={500}
        shapes={[shape1, shape2]}></WaterfallPlot>
      <Text>
        <ColorSpan color={GeckoTheme.colors.SeaGreen}>The Inclusion </ColorSpan>
        is visible in the 45° B-Scan , but it does not block{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>the I.D.</ColorSpan>,
        meaning sound is still able to penetrate through. In the event of HIC,
        we expect{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>the I.D.</ColorSpan>{' '}
        to be completely attenuated under the inclusion, and for the signal to
        propagate up from the edges in the direction of stress.{' '}
      </Text>
    </div>
  )
}

const DRA2VerdictItem: FC = () => {
  return (
    <div>
      <AntTitle level={4}>
        Gecko Verdict:{' '}
        <ColorSpan color={GeckoTheme.colors.BrightOrange}>
          DRA Level 2
        </ColorSpan>
      </AntTitle>
      <Text strong>
        Based on the above notes, this indication is an Inclusion with Hydrogen
        Charging, but not HIC
      </Text>
    </div>
  )
}

const DRA2Actions: FC = () => {
  return (
    <div>
      <AntTitle level={4}>
        Recommended Action:{' '}
        <ColorSpan color={GeckoTheme.colors.LemonYellow}>Monitor</ColorSpan>
      </AntTitle>
      <Text strong>
        There are early signs that HIC could develop in this area. Gecko
        recommends medium-term monitoring of this area.
      </Text>
      <GridContainer>
        <Button onClick={() => message.success('Action Accepted!')}>
          Accept
        </Button>
        <Button>Reject</Button>
      </GridContainer>
    </div>
  )
}

export const Title = () => 'TriLat Plots'
export const Width = '24rem'
export const CardView: FC<IndicationDetailCardProps> = () => {
  const { indication: item } = useIndicationData()

  const { data, isLoading } = useIndicationFiles(item?.id)

  return (
    <div>
      <Spin indicator={<LoadingOutlined />} size='large' spinning={isLoading}>
        <div>
          {!isEmpty(data) ? (
            <DRA2View
              bscan0={data['bscan_0']}
              bscan1={data['bscan_1']}
              bscan2={data['bscan_2']}
            />
          ) : isLoading ? (
            <div />
          ) : (
            <Text>No Data</Text>
          )}
        </div>
      </Spin>
    </div>
  )
}

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: repeat(4, max-content);
  align-items: center;
  padding-top: 8px;
  gap: 2px 8%;
`
const ColumnWrapper = styled.div`
  display: flex;
  flex-direction: column; /* Arrange children vertically */
  gap: 10px; /* Add space between each child (div) */
  padding: 10px; /* Optional: Add some padding inside the container */
`

const ColorSpan = styled.span<{ color: string }>`
  color: ${({ color }) => color};
`
